import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Sceglie l'header Authorization in base al PREFISSO del percorso, non a
 * sottostringhe.
 *
 * Bug risolto: le route CRM (`/api/crm/...`) devono SEMPRE usare il token CRM,
 * anche quando il path contiene parole come "customers" o "orders"
 * (es. `/api/crm/customers`, `/api/crm/ecommerce/orders`). Con la vecchia regola
 * a `includes('/customer')` / `includes('/orders')`, se nel browser era presente
 * anche un `customer_token` (un admin loggato pure come cliente del negozio), le
 * richieste CRM partivano con il token sbagliato e ricevevano 401.
 *
 * Sul bucket "tutto il resto" NON si può applicare un routing binario secco
 * "non-CRM ⇒ token cliente": in questo codebase esistono endpoint protetti dal
 * token CRM che vivono FUORI dal prefisso `/api/crm/`. Esempio reale verificato:
 * `POST /api/uploads/request-url` è guardato da `requireAuth` (auth CRM) ed è
 * chiamato via `apiRequest` dalla pagina admin `crm-profile.tsx`. Mandargli il
 * token cliente lo farebbe fallire con 401. Per questo il bucket residuo usa
 * il token CRM se presente (così gli endpoint admin "nudi" funzionano), poi il
 * token cliente come ripiego, infine nessun header per le route pubbliche.
 */
type TokenType = "crm" | "customer" | "none";

interface SelectedToken {
  type: TokenType;
  token: string | null;
}

/**
 * Determina QUALE token usare (e di che TIPO) in base al PREFISSO del path, con
 * la stessa logica documentata sopra. Espone il `type` così che il layer di
 * refresh sappia quale famiglia di token rinnovare in caso di 401.
 */
function selectToken(url: string): SelectedToken {
  const customerToken = localStorage.getItem('customer_token');
  const crmToken = localStorage.getItem('crm_token');

  // 1) Route CRM: sempre ed esclusivamente il token CRM.
  //    Prefisso con slash finale per non intercettare path tipo `/api/crmX`.
  if (url === '/api/crm' || url.startsWith('/api/crm/')) {
    return { type: 'crm', token: crmToken };
  }

  // 2) Route dell'area cliente/negozio: famiglia di token CLIENTE.
  //    Nota: gli endpoint autenticati dell'account cliente vivono sotto
  //    `/api/auth/customer/...` (es. profilo, cambio password), quindi vanno
  //    inclusi qui — la vecchia regola li intercettava via includes('/customer').
  //    IMPORTANTE: si restituisce SEMPRE `type:'customer'` per queste route,
  //    anche quando il `customer_token` è assente. Così su 401 il refresh usa la
  //    famiglia giusta (cliente) e non si rischia mai di mandare il bearer CRM a
  //    un endpoint cliente cadendo nel bucket residuo qui sotto.
  const isCustomerRoute =
    url.startsWith('/api/cart') ||
    url.startsWith('/api/orders') ||
    url.startsWith('/api/customer') ||
    url.startsWith('/api/auth/customer');
  if (isCustomerRoute) {
    return { type: 'customer', token: customerToken };
  }

  // 3) Tutto il resto: endpoint admin "nudi" (token CRM) o pubblici (nessun
  //    token richiesto). Si privilegia il token CRM se presente, poi quello
  //    cliente come ripiego, infine nessun header.
  if (crmToken) {
    return { type: 'crm', token: crmToken };
  }
  if (customerToken) {
    return { type: 'customer', token: customerToken };
  }
  return { type: 'none', token: null };
}

function buildAuthHeaders(url: string): Record<string, string> {
  const { token } = selectToken(url);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Refresh-and-retry unificato
// ---------------------------------------------------------------------------
// Tutte le richieste autenticate passano da `fetchWithAuthRetry`. Su 401, se
// esiste un refresh token per la famiglia (CRM o cliente), si tenta UN solo
// refresh (single-flight: la stessa POST è condivisa anche con N richieste in
// volo), poi si ritenta la richiesta originale UNA volta. Se il refresh
// fallisce si puliscono i token e si notifica l'auth context registrato per un
// logout "soft" (redirect via wouter) senza hard-reload che azzera la UI.

const REFRESH_ENDPOINTS: Record<'crm' | 'customer', string> = {
  crm: '/api/crm/auth/refresh',
  customer: '/api/auth/customer/refresh',
};

const TOKEN_KEYS: Record<'crm' | 'customer', { token: string; refresh: string }> = {
  crm: { token: 'crm_token', refresh: 'crm_refresh_token' },
  customer: { token: 'customer_token', refresh: 'customer_refresh_token' },
};

function isRefreshEndpoint(url: string): boolean {
  return url === REFRESH_ENDPOINTS.crm || url === REFRESH_ENDPOINTS.customer;
}

type RefreshResult = boolean | "stale";

const refreshPromises: Record<'crm' | 'customer', Promise<RefreshResult> | null> = {
  crm: null,
  customer: null,
};
const refreshFlightMeta: Record<
  'crm' | 'customer',
  { refreshToken: string; generation: number } | null
> = { crm: null, customer: null };

let customerSessionFingerprint: string | null = null;
let customerSessionGeneration = 0;

function observeCustomerSession(): void {
  const fingerprint = `${localStorage.getItem(TOKEN_KEYS.customer.token) ?? ""}\u0000${localStorage.getItem(TOKEN_KEYS.customer.refresh) ?? ""}`;
  if (fingerprint !== customerSessionFingerprint) {
    customerSessionFingerprint = fingerprint;
    customerSessionGeneration++;
  }
}

function isCurrentCustomerSession(generation: number, refreshToken: string | null): boolean {
  observeCustomerSession();
  return customerSessionGeneration === generation &&
    localStorage.getItem(TOKEN_KEYS.customer.refresh) === refreshToken;
}

function isCurrentCustomerRequest(
  generation: number | null,
  accessToken: string | null,
  refreshToken: string | null,
): boolean {
  if (generation === null) return false;
  observeCustomerSession();
  return customerSessionGeneration === generation &&
    localStorage.getItem(TOKEN_KEYS.customer.token) === accessToken &&
    localStorage.getItem(TOKEN_KEYS.customer.refresh) === refreshToken;
}

async function refreshTokenFamily(type: 'crm' | 'customer'): Promise<RefreshResult> {
  const keys = TOKEN_KEYS[type];
  const refreshToken = localStorage.getItem(keys.refresh);
  if (!refreshToken) return false;
  const generation = type === "customer" ? (observeCustomerSession(), customerSessionGeneration) : 0;
  const existing = refreshPromises[type];
  const existingMeta = refreshFlightMeta[type];
  if (
    existing &&
    (type !== "customer" ||
      (existingMeta?.refreshToken === refreshToken &&
        existingMeta.generation === generation))
  ) {
    return existing;
  }
  // The old customer flight is deliberately left running, but it is no longer
  // shared with the new identity. Its finally block cannot clear this flight.

  const promise = (async () => {
    try {
      const res = await fetch(REFRESH_ENDPOINTS[type], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include',
      });
      if (!res.ok) {
        return type === "customer" && !isCurrentCustomerSession(generation, refreshToken)
          ? "stale"
          : false;
      }
      const data = await res.json().catch(() => null);
      if (!data?.token) {
        return type === "customer" && !isCurrentCustomerSession(generation, refreshToken)
          ? "stale"
          : false;
      }
      if (
        type === "customer" &&
        !isCurrentCustomerSession(generation, refreshToken)
      ) {
        return "stale";
      }
      localStorage.setItem(keys.token, data.token);
      if (data.refreshToken) {
        localStorage.setItem(keys.refresh, data.refreshToken);
      }
      if (type === "customer") observeCustomerSession();
      return true;
    } catch {
      return type === "customer" && !isCurrentCustomerSession(generation, refreshToken)
        ? "stale"
        : false;
    }
  })();

  refreshPromises[type] = promise;
  refreshFlightMeta[type] = { refreshToken, generation };
  try {
    return await promise;
  } finally {
    // A logout/login can install a newer flight while this one is pending.
    // Never let the old flight clear that newer promise.
    if (refreshPromises[type] === promise) {
      refreshPromises[type] = null;
      refreshFlightMeta[type] = null;
    }
  }
}

type AuthFailureHandler = () => void;
const authFailureHandlers: Record<'crm' | 'customer', AuthFailureHandler | null> = {
  crm: null,
  customer: null,
};

/**
 * Permette agli auth context React di registrare un logout "soft" (clear dello
 * stato + redirect via wouter) che il transport può invocare quando il refresh
 * fallisce, evitando `window.location.href` che resetterebbe tutta la UI.
 */
export function setAuthFailureHandler(
  type: 'crm' | 'customer',
  handler: AuthFailureHandler | null,
): void {
  authFailureHandlers[type] = handler;
}

function handleAuthFailure(type: 'crm' | 'customer'): void {
  const keys = TOKEN_KEYS[type];
  localStorage.removeItem(keys.token);
  localStorage.removeItem(keys.refresh);
  authFailureHandlers[type]?.();
}

async function fetchWithAuthRetry(
  url: string,
  init: RequestInit,
  allowRetry = true,
): Promise<Response> {
  const { type } = selectToken(url);
  observeCustomerSession();
  let requestCustomerGeneration =
    type === "customer" ? customerSessionGeneration : null;
  let requestRefreshToken =
    type === "customer" ? localStorage.getItem(TOKEN_KEYS.customer.refresh) : null;
  let requestCustomerToken =
    type === "customer" ? localStorage.getItem(TOKEN_KEYS.customer.token) : null;
  const baseHeaders = (init.headers as Record<string, string> | undefined) ?? {};

  const res = await fetch(url, {
    ...init,
    headers: { ...baseHeaders, ...buildAuthHeaders(url) },
    credentials: 'include',
  });

  // Do this fence before looking at refreshPromises. An old A request must
  // never borrow B's credentials to refresh or retry its mutation.
  if (
    res.status === 401 &&
    type === "customer" &&
    !isRefreshEndpoint(url) &&
    !init.signal?.aborted &&
    !isCurrentCustomerRequest(
      requestCustomerGeneration,
      requestCustomerToken,
      requestRefreshToken,
    )
  ) {
    return res;
  }

  if (
    res.status === 401 &&
    allowRetry &&
    !init.signal?.aborted &&
    (type === 'crm' || type === 'customer') &&
    !isRefreshEndpoint(url) &&
    localStorage.getItem(TOKEN_KEYS[type].refresh)
  ) {
    const refreshed = await refreshTokenFamily(type);
    if (
      refreshed === "stale" ||
      init.signal?.aborted
    ) {
      return res;
    }
    // A failed old refresh must not soft-logout a newer customer session that
    // replaced its credentials while the request was awaiting the server.
    if (
      refreshed === false &&
      type === "customer" &&
      !isCurrentCustomerRequest(
        requestCustomerGeneration,
        requestCustomerToken,
        requestRefreshToken,
      )
    ) {
      return res;
    }
    if (refreshed) {
      if (type === "customer") {
        observeCustomerSession();
        requestCustomerGeneration = customerSessionGeneration;
        requestRefreshToken = localStorage.getItem(TOKEN_KEYS.customer.refresh);
        requestCustomerToken = localStorage.getItem(TOKEN_KEYS.customer.token);
      }
      const retryRes = await fetch(url, {
        ...init,
        headers: { ...baseHeaders, ...buildAuthHeaders(url) },
        credentials: 'include',
      });
      // Se anche dopo un refresh riuscito il retry torna 401, il token nuovo non
      // è comunque autorizzato: è un fallimento terminale, quindi logout soft e
      // pulizia dei token (altrimenti resterebbero in storage credenziali non
      // valide).
      if (
        retryRes.status === 401 &&
        (type !== "customer" ||
          (requestCustomerGeneration === customerSessionGeneration &&
            isCurrentCustomerRequest(
              requestCustomerGeneration,
              requestCustomerToken,
              requestRefreshToken,
            )))
      ) {
        handleAuthFailure(type);
      }
      return retryRes;
    }
    handleAuthFailure(type);
  }

  // A customer access token without a refresh token is still an authenticated
  // session. Treat its 401 as terminal, but only when the captured identity is
  // still current; an old tab/request must never log out the newer customer.
  if (
    res.status === 401 &&
    allowRetry &&
    !init.signal?.aborted &&
    type === "customer" &&
    !isRefreshEndpoint(url) &&
    requestCustomerToken &&
    requestCustomerGeneration !== null &&
    isCurrentCustomerRequest(
      requestCustomerGeneration,
      requestCustomerToken,
      requestRefreshToken,
    )
  ) {
    handleAuthFailure("customer");
  }

  return res;
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Already encoded body, used by the lossless gzip transport. */
  preparedBody?: BodyInit;
  contentType?: string;
  contentEncoding?: string;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const headers: Record<string, string> = { ...options.headers };

  if (data !== undefined || options.preparedBody !== undefined) {
    headers["Content-Type"] = options.contentType ?? "application/json";
  }
  if (options.contentEncoding) headers["Content-Encoding"] = options.contentEncoding;

  const res = await fetchWithAuthRetry(url, {
    method,
    headers,
    body: options.preparedBody ?? (data !== undefined ? JSON.stringify(data) : undefined),
    signal: options.signal,
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Variante che restituisce JSON già parsato, replicando il contratto della
 * vecchia `apiRequest` di `crm-api` (204 → `{ success: true }`, e in caso di
 * errore lancia l'oggetto errore parsato dal server invece di una `Error`
 * generica) ma passando dal transport unificato con refresh-and-retry.
 *
 * Firma allineata alla `apiRequest` canonica: `(method, url, data?)`.
 */
export async function apiRequestJson<T = any>(
  method: string,
  url: string,
  data?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (data !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetchWithAuthRetry(url, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (parsed && typeof parsed === "object") {
      throw parsed;
    }
    throw new Error(`Request failed: ${res.statusText}`);
  }

  // 204 No Content (es. DELETE) → mantiene il vecchio contratto.
  if (res.status === 204) {
    return { success: true } as T;
  }
  const text = await res.text();
  if (!text) {
    return { success: true } as T;
  }
  return JSON.parse(text) as T;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const url = queryKey.join("/") as string;

    const res = await fetchWithAuthRetry(url, { method: "GET", signal });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Private customer queries are removed on every customer identity transition.
 * This is intentionally narrower than QueryClient.clear(): CRM and public
 * caches can remain warm while customer data cannot cross accounts.
 */
export function isCustomerPrivateQueryKey(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0];
  return typeof root === "string" && (
    root.startsWith("/api/cart") ||
    root.startsWith("/api/orders") ||
    root.startsWith("/api/customer") ||
    root.startsWith("/api/auth/customer")
  );
}

export function resetCustomerPrivateQueries(): void {
  const filters = {
    predicate: (query: { queryKey: readonly unknown[] }) =>
      isCustomerPrivateQueryKey(query.queryKey),
  };

  // cancelQueries propagates QueryFunctionContext.signal to fetch, so an old
  // request cannot commit after logout/login. Remove immediately as well:
  // logout is intentionally synchronous from the UI's point of view.
  void queryClient.cancelQueries(filters);
  if (typeof queryClient.removeQueries === "function") {
    queryClient.removeQueries(filters);
  } else {
    // Kept only for minimal test doubles that predate QueryClient.removeQueries.
    queryClient.clear();
  }
}

export async function resetCustomerPrivateQueriesAsync(): Promise<void> {
  const filters = {
    predicate: (query: { queryKey: readonly unknown[] }) =>
      isCustomerPrivateQueryKey(query.queryKey),
  };
  await queryClient.cancelQueries(filters);
  if (typeof queryClient.removeQueries === "function") {
    queryClient.removeQueries(filters);
  } else {
    queryClient.clear();
  }
}

/**
 * Tier ufficiali per `staleTime` di TanStack Query.
 *
 * Scegliere il tier in base alla volatilità della risorsa:
 *  - `live`     (10s)   eventi quasi-realtime (notifiche, system health)
 *  - `fresh`    (30s)   default: liste CRM, banner, dashboard counters
 *                       (con `refetchOnWindowFocus: true` l'utente che
 *                        rientra in tab dopo aver fatto una modifica vede
 *                        i nuovi dati entro pochi secondi).
 *  - `quotations` (60s) listini in tempo reale, cache server allineata
 *  - `catalog`  (5 min) cataloghi pubblici, dettagli prodotto navigati
 *  - `reference`(10 min) dati di riferimento (brands, financing tiers,
 *                        coin/lingotti config) modificati raramente
 *  - `static`   (30 min) contenuti statici (OAM, sitemap, asset versionati)
 *  - `user`     (2 min)  dati utente non collaborativi (carrello, ordini)
 *
 * Vedi `docs/audits/cache-strategy.md` per la matrice completa
 * (task #62 + task #73).
 */
export const CACHE_TIMES = {
  live: 10 * 1000,
  fresh: 30 * 1000,
  quotations: 60 * 1000,
  catalog: 5 * 60 * 1000,
  reference: 10 * 60 * 1000,
  settings: 10 * 60 * 1000,
  static: 30 * 60 * 1000,
  user: 2 * 60 * 1000,
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      // Stale-While-Revalidate: mostra dati cache immediatamente, aggiorna in background
      refetchOnWindowFocus: true, // Ricarica quando utente torna alla tab
      refetchOnReconnect: true, // Ricarica quando torna online
      refetchOnMount: "always", // Verifica sempre se ci sono aggiornamenti
      // Default `fresh` (30s): combinato con `refetchOnWindowFocus`, garantisce
      // che chiunque modifichi una risorsa nel CRM la rivedrà aggiornata
      // entro pochi secondi rientrando nel tab senza dover ricaricare la pagina.
      // Le query che possono permettersi un TTL più lungo (cataloghi pubblici,
      // dati di riferimento, contenuti statici) devono dichiararlo
      // esplicitamente con `staleTime: CACHE_TIMES.<tier>`.
      staleTime: CACHE_TIMES.fresh,
      gcTime: 10 * 60 * 1000,
      retry: 1, // Un retry automatico per resilienza
      retryDelay: 1000,
    },
    mutations: {
      retry: false,
    },
  },
});
